import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { 
  MessageSquare,
  Send,
  Bot,
  User,
  Target,
  Clock,
  FileVideo,
  X,
  Lightbulb,
  Zap,
  Scissors,
  Move,
  Volume2,
  Sparkles,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

/**
 * Selected Clips Chat Component
 * 
 * Provides context-aware chat interface for editing selected clips.
 * Shows selection context, smart suggestions, and handles natural language commands.
 */
const SelectedClipsChat = ({ 
  selectedClips = [],
  selectionContext = null,
  onChatMessage,
  onCloseChat,
  isProcessing = false,
  className = ''
}) => {
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  // Focus input when component mounts
  useEffect(() => {
    if (!isMinimized) {
      inputRef.current?.focus();
    }
  }, [isMinimized]);

  // Generate context-aware suggestions based on selected clips
  const generateSmartSuggestions = useCallback(() => {
    if (!selectionContext || selectedClips.length === 0) return [];

    const suggestions = [];
    const { clipCount, totalDuration, tags } = selectionContext;

    // Duration-based suggestions
    if (totalDuration > 30) {
      suggestions.push({
        icon: Scissors,
        text: "Cut to 30 seconds",
        command: "Cut these clips to 30 seconds keeping the best parts",
        category: "duration"
      });
    }

    if (totalDuration > 10) {
      suggestions.push({
        icon: Zap,
        text: "Speed up 2x",
        command: "Speed up these clips to 2x playback speed",
        category: "timing"
      });
    }

    // Multi-clip suggestions
    if (clipCount > 1) {
      suggestions.push({
        icon: Move,
        text: "Arrange by energy",
        command: "Arrange these clips from low to high energy",
        category: "arrangement"
      });

      suggestions.push({
        icon: Target,
        text: "Create montage",
        command: "Create a quick montage from these clips",
        category: "creation"
      });
    }

    // Content-based suggestions
    if (tags.includes('talking')) {
      suggestions.push({
        icon: Volume2,
        text: "Remove silence",
        command: "Remove silent parts from these talking clips",
        category: "audio"
      });
    }

    if (tags.includes('action')) {
      suggestions.push({
        icon: Sparkles,
        text: "Add transitions",
        command: "Add dynamic transitions between these action clips",
        category: "effects"
      });
    }

    // Position-based suggestions
    suggestions.push({
      icon: Move,
      text: "Move to beginning",
      command: "Move these clips to the beginning of the timeline",
      category: "position"
    });

    suggestions.push({
      icon: Target,
      text: "Center in timeline",
      command: "Center these clips in the middle of the timeline",
      category: "position"
    });

    return suggestions.slice(0, 6); // Limit to 6 suggestions
  }, [selectionContext, selectedClips]);

  const smartSuggestions = generateSmartSuggestions();

  const handleSendMessage = useCallback(async () => {
    if (!message.trim() || isProcessing) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
      selectionContext: selectionContext
    };

    setChatHistory(prev => [...prev, userMessage]);
    setMessage('');

    try {
      // Call parent handler with message and selection context
      const response = await onChatMessage?.(message, {
        selectedClips,
        selectionContext,
        chatHistory
      });

      if (response) {
        setChatHistory(prev => [...prev, {
          id: Date.now() + 1,
          role: 'assistant',
          content: response.content || response.message,
          code: response.code,
          timestamp: new Date().toISOString(),
          metadata: response.metadata
        }]);
      }
    } catch (error) {
      setChatHistory(prev => [...prev, {
        id: Date.now() + 1,
        role: 'assistant',
        content: `Sorry, I encountered an error: ${error.message}`,
        timestamp: new Date().toISOString(),
        error: true
      }]);
    }
  }, [message, isProcessing, selectionContext, selectedClips, chatHistory, onChatMessage]);

  const handleSuggestionClick = useCallback((suggestion) => {
    setMessage(suggestion.command);
    inputRef.current?.focus();
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!selectionContext || selectedClips.length === 0) {
    return null;
  }

  return (
    <div className={`bg-zinc-800 border border-zinc-600 rounded-lg shadow-lg ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-zinc-600">
        <div className="flex items-center space-x-2">
          <MessageSquare className="w-4 h-4 text-blue-400" />
          <span className="font-medium text-white">Selected Clips Chat</span>
          <Badge variant="outline" className="text-xs">
            {selectedClips.length} clip{selectedClips.length !== 1 ? 's' : ''}
          </Badge>
        </div>
        <div className="flex items-center space-x-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setIsMinimized(!isMinimized)}
            className="text-gray-400 hover:text-white"
          >
            {isMinimized ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onCloseChat}
            className="text-gray-400 hover:text-white"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Selection Summary */}
          <div className="p-3 bg-zinc-900 border-b border-zinc-600">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <div className="text-xs text-gray-400">Clips</div>
                <div className="font-bold text-white">{selectionContext.clipCount}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400">Duration</div>
                <div className="font-bold text-white">{formatTime(selectionContext.totalDuration)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400">Range</div>
                <div className="font-bold text-white text-xs">
                  {formatTime(selectionContext.timeRange.start)}-{formatTime(selectionContext.timeRange.end)}
                </div>
              </div>
            </div>
            
            {selectionContext.tags.length > 0 && (
              <div className="mt-2">
                <div className="text-xs text-gray-400 mb-1">Content</div>
                <div className="flex flex-wrap gap-1">
                  {selectionContext.tags.map(tag => (
                    <span key={tag} className="text-xs bg-blue-600 text-white px-2 py-1 rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Smart Suggestions */}
          {showSuggestions && smartSuggestions.length > 0 && (
            <div className="p-3 border-b border-zinc-600">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <Lightbulb className="w-3 h-3 text-yellow-400" />
                  <span className="text-xs font-medium text-gray-300">Smart Suggestions</span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowSuggestions(false)}
                  className="text-xs text-gray-400 hover:text-white p-1"
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {smartSuggestions.map((suggestion, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="h-auto p-2 text-xs flex items-start justify-start text-left"
                  >
                    <suggestion.icon className="w-3 h-3 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="truncate">{suggestion.text}</span>
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Chat Messages */}
          <ScrollArea className="h-48 p-3">
            <div className="space-y-3">
              {chatHistory.length === 0 && (
                <div className="text-center text-gray-400 text-sm py-4">
                  <Bot className="w-6 h-6 mx-auto mb-2 opacity-50" />
                  <p>Tell me what you'd like to do with these {selectedClips.length} clips.</p>
                  <p className="text-xs mt-1">Try: "Speed this up", "Add transitions", or "Move to beginning"</p>
                </div>
              )}
              
              {chatHistory.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-xs p-2 rounded text-sm ${
                      msg.role === 'user'
                        ? 'bg-blue-600 text-white'
                        : msg.error
                        ? 'bg-red-900 text-red-100'
                        : 'bg-zinc-700 text-gray-100'
                    }`}
                  >
                    <div className="flex items-center space-x-1 mb-1">
                      {msg.role === 'user' ? (
                        <User className="w-3 h-3" />
                      ) : (
                        <Bot className="w-3 h-3" />
                      )}
                      <span className="text-xs opacity-75">
                        {new Date(msg.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    <p>{msg.content}</p>
                    
                    {/* Intent Analysis for Selected Clips */}
                    {msg.intentAnalysis && (
                      <div className="mt-1 p-1 bg-zinc-600 rounded text-xs">
                        <div className="flex items-center space-x-1 mb-1">
                          <Target className="w-2 h-2 text-blue-400" />
                          <span className="text-blue-200 text-xs">Intent</span>
                        </div>
                        <div className="text-gray-300 text-xs">
                          {msg.intentAnalysis.primary_goal} • {msg.intentAnalysis.complexity}
                          {msg.intentAnalysis.operations && msg.intentAnalysis.operations.length > 0 && (
                            <div className="mt-1">
                              {msg.intentAnalysis.operations.map((op, i) => (
                                <span key={i} className="text-yellow-200 text-xs mr-1">
                                  {op.action}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {msg.code && (
                      <div className="mt-2 text-xs">
                        <span className="text-blue-300">Code generated</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {isProcessing && (
                <div className="flex justify-start">
                  <div className="bg-zinc-700 text-gray-100 p-2 rounded text-sm">
                    <div className="flex items-center space-x-2">
                      <Bot className="w-3 h-3" />
                      <div className="flex space-x-1">
                        <div className="w-1 h-1 bg-blue-400 rounded-full animate-bounce"></div>
                        <div className="w-1 h-1 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-1 h-1 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div ref={chatEndRef} />
          </ScrollArea>

          {/* Message Input */}
          <div className="p-3 border-t border-zinc-600">
            <div className="flex space-x-2">
              <Input
                ref={inputRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell me what to do with these clips..."
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                disabled={isProcessing}
                className="flex-1 bg-zinc-900 border-zinc-600 text-white text-sm"
              />
              <Button
                onClick={handleSendMessage}
                disabled={isProcessing || !message.trim()}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Context: {selectedClips.length} clips selected • Duration: {formatTime(selectionContext.totalDuration)}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SelectedClipsChat;